import React, { useEffect, useRef } from "react";
import { VLYT } from "@/constants/testIds/vlyt";

/**
 * Pulse of Hebron — reactive dot grid canvas.
 * - Dots draw as low-opacity purple/lime.
 * - Mouse proximity brightens dots to cyber lime with radial glow.
 * - Autonomous ripple emanates from a random dot every 3.2s.
 * - Fallback: static grid on prefers-reduced-motion or low-power devices.
 */
export default function PulseCanvas() {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const stateRef = useRef({
    dots: [],
    mouse: { x: -9999, y: -9999, active: false },
    ripples: [],
    dpr: 1,
    w: 0,
    h: 0,
    lastAuto: 0,
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const lowPower = (navigator.hardwareConcurrency || 4) <= 2;

    const ctx = canvas.getContext("2d");
    const state = stateRef.current;

    const SPACING = 28;

    function readThemeColors() {
      const styles = getComputedStyle(document.documentElement);
      const base = (styles.getPropertyValue("--vlyt-dot-base-rgb") || "168, 123, 255").trim();
      const hot = (styles.getPropertyValue("--vlyt-dot-hot-rgb") || "191, 255, 0").trim();
      const bg = (styles.getPropertyValue("--vlyt-canvas-bg") || "").trim();
      const parse = (s) => s.split(",").map((n) => parseFloat(n.trim()));
      state.dotBase = parse(base);
      state.dotHot = parse(hot);
      if (bg) canvas.style.background = bg;
    }
    readThemeColors();
    const themeObserver = new MutationObserver(readThemeColors);
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });

    function resize() {
      const rect = canvas.getBoundingClientRect();
      state.dpr = Math.min(window.devicePixelRatio || 1, 2);
      state.w = rect.width;
      state.h = rect.height;
      canvas.width = rect.width * state.dpr;
      canvas.height = rect.height * state.dpr;
      ctx.setTransform(state.dpr, 0, 0, state.dpr, 0, 0);
      const cols = Math.ceil(state.w / SPACING) + 2;
      const rows = Math.ceil(state.h / SPACING) + 2;
      state.dots = [];
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          state.dots.push({
            x: c * SPACING,
            y: r * SPACING,
            brightness: 0,
          });
        }
      }
    }

    function handleMouse(e) {
      const rect = canvas.getBoundingClientRect();
      state.mouse.x = e.clientX - rect.left;
      state.mouse.y = e.clientY - rect.top;
      state.mouse.active = true;
    }
    function handleLeave() {
      state.mouse.active = false;
      state.mouse.x = -9999;
      state.mouse.y = -9999;
    }

    function spawnAutoRipple(now) {
      const rx = Math.random() * state.w;
      const ry = Math.random() * state.h * 0.9 + state.h * 0.05;
      state.ripples.push({ x: rx, y: ry, start: now, life: 1600 });
    }

    function drawStatic() {
      ctx.clearRect(0, 0, state.w, state.h);
      const [br, bg, bb] = state.dotBase || [168, 123, 255];
      ctx.fillStyle = `rgba(${br}, ${bg}, ${bb}, 0.22)`;
      state.dots.forEach((d) => {
        ctx.beginPath();
        ctx.arc(d.x, d.y, 1.1, 0, Math.PI * 2);
        ctx.fill();
      });
    }

    function draw(now) {
      const s = state;
      ctx.clearRect(0, 0, s.w, s.h);

      // Autonomous ripple every 3.2s
      if (!reduced && now - s.lastAuto > 3200) {
        spawnAutoRipple(now);
        s.lastAuto = now;
      }

      // Compute ripple influences
      const ripples = s.ripples.filter((r) => now - r.start < r.life);
      s.ripples = ripples;

      // Draw dots
      const mx = s.mouse.x, my = s.mouse.y;
      const mouseRadius = 140;
      for (let i = 0; i < s.dots.length; i++) {
        const d = s.dots[i];
        let brightness = 0.14; // base purple-ish
        let colorMix = 0; // 0 = purple, 1 = lime

        if (s.mouse.active) {
          const dx = d.x - mx;
          const dy = d.y - my;
          const dist = Math.hypot(dx, dy);
          if (dist < mouseRadius) {
            const t = 1 - dist / mouseRadius;
            brightness = Math.max(brightness, 0.2 + t * 0.85);
            colorMix = Math.max(colorMix, t);
          }
        }

        // ripples
        for (let j = 0; j < ripples.length; j++) {
          const r = ripples[j];
          const age = (now - r.start) / r.life; // 0..1
          const radius = age * 260;
          const dist = Math.hypot(d.x - r.x, d.y - r.y);
          const band = Math.abs(dist - radius);
          if (band < 22) {
            const strength = (1 - band / 22) * (1 - age);
            brightness = Math.max(brightness, 0.25 + strength * 0.85);
            colorMix = Math.max(colorMix, strength * 0.8);
          }
        }

        // color: interpolate between theme base (cold) and theme hot (lime)
        const [br, bg, bb] = state.dotBase || [168, 123, 255];
        const [hr, hg, hb] = state.dotHot || [191, 255, 0];
        const R = br + (hr - br) * colorMix;
        const G = bg + (hg - bg) * colorMix;
        const B = bb + (hb - bb) * colorMix;
        ctx.fillStyle = `rgba(${R|0}, ${G|0}, ${B|0}, ${brightness})`;
        ctx.beginPath();
        ctx.arc(d.x, d.y, 1.2 + brightness * 1.6, 0, Math.PI * 2);
        ctx.fill();

        // glow halo on bright dots
        if (brightness > 0.55) {
          ctx.fillStyle = `rgba(${hr}, ${hg}, ${hb}, ${(brightness - 0.55) * 0.35})`;
          ctx.beginPath();
          ctx.arc(d.x, d.y, 6 + brightness * 6, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      rafRef.current = requestAnimationFrame(draw);
    }

    resize();
    if (reduced || lowPower) {
      drawStatic();
    } else {
      rafRef.current = requestAnimationFrame(draw);
    }

    window.addEventListener("resize", resize);
    canvas.addEventListener("pointermove", handleMouse);
    canvas.addEventListener("pointerleave", handleLeave);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
      canvas.removeEventListener("pointermove", handleMouse);
      canvas.removeEventListener("pointerleave", handleLeave);
      themeObserver.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid={VLYT.hero.canvas}
      aria-hidden="true"
      className="absolute inset-0 w-full h-full"
    />
  );
}
